import React from 'react';
import { array, bool, node, object, oneOf, oneOfType, shape, string } from 'prop-types';
import classNames from 'classnames';
import { propTypes } from '../../../util/types';
import { routeForSearchTab } from '../../../util/searchListingTabs';
import { ListingCard, PaginationLinks } from '../../../components';
import css from './SearchResultsPanel.module.css';

const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings,
    pagination,
    search,
    setActiveListing,
    isMapVariant,
    currentUser,
    isAuthenticated,
    onupdateProfile,
    history,
    reviews,
    onFetchReviews,
    tab,
    searchCapacityGuestRange,
    location,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  // Preserve the active category tab across pagination (/s, /s/todos, /s/jardines, …).
  const { routeName: pageName, params: pagePathParams } = routeForSearchTab(tab);

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={pagePathParams}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  // When the duck delivers more listings than the page size (merged response from
  // bounds subdivision), paginate client-side so the grid stays at perPage cards.
  // Server-paginated responses already arrive sliced, so the condition is a no-op.
  const perPage = pagination?.perPage;
  const currentPage = pagination?.page || 1;
  const pageListings =
    perPage && listings.length > perPage
      ? listings.slice((currentPage - 1) * perPage, currentPage * perPage)
      : listings;

  // Superhost priority is applied globally in SearchPage.duck (over the full result
  // set, cascading across pages) before this client-side slice, so the page is shown
  // as-is here.
  const visibleListings = pageListings;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 57; // SearchPage.module.css: .searchResultContainer
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {visibleListings.map(l => (
          <ListingCard
            className={css.listingCard}
            key={l.id.uuid}
            listing={l}
            openListingInNewTab
            renderSizes={cardRenderSizes(isMapVariant)}
            setActiveListing={setActiveListing}
            listingIds={visibleListings.map(l => l.id.uuid)}
            currentUser={currentUser}
            listingId={l.id.uuid}
            isAuthenticated={isAuthenticated}
            onupdateProfile={onupdateProfile}
            history={history}
            location={location}
            reviews={l?.attributes?.publicData?.noReviews ?? 0}
            onFetchReviews={onFetchReviews}
            searchCapacityGuestRange={searchCapacityGuestRange}
          />
        ))}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

SearchResultsPanel.defaultProps = {
  children: null,
  className: null,
  listings: [],
  pagination: null,
  rootClassName: null,
  search: null,
  isMapVariant: true,
  searchCapacityGuestRange: null,
  location: null,
};

SearchResultsPanel.propTypes = {
  children: node,
  className: string,
  listings: array,
  pagination: propTypes.pagination,
  rootClassName: string,
  search: object,
  isMapVariant: bool,
  searchCapacityGuestRange: oneOfType([string, oneOf([null])]),
  location: shape({ search: string }),
};

export default SearchResultsPanel;
